import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { IconLanguage, IconLogin2, IconUserPlus, IconMenu2, IconX } from "@tabler/icons-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";

export default function SiteNav() {
  const { t, lang, toggle } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  // Close menu on route change and lock body scroll while open
  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const linkCls = (p) =>
    `text-[13px] tracking-wide transition ${
      loc.pathname === p ? "text-teal-deep" : "text-muted hover:text-teal-deep"
    }`;

  const mobileLinkCls = (p) =>
    `block px-5 py-3 text-[15px] font-medium border-b border-line ${
      loc.pathname === p ? "text-teal-deep bg-teal-soft/50" : "text-muted"
    }`;

  return (
    <>
      <header className="glass-bar sticky top-0 z-40 h-14">
        <div className="wrap h-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="site-nav-brand">
            <BrandLogo />
            <span className="font-display text-[15px] sm:text-[17px] tracking-tight">
              Chiessa<span className="text-honey">.</span> Institute
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className={linkCls("/")} data-testid="site-nav-home">
              {t("nav.home")}
            </Link>
            <Link to="/nosotros" className={linkCls("/nosotros")} data-testid="site-nav-about">
              {t("nav.about")}
            </Link>
            <Link to="/psicogadgets" className={linkCls("/psicogadgets")} data-testid="site-nav-gadgets">
              {t("nav.gadgets")}
            </Link>
            <Link to="/productos" className={linkCls("/productos")} data-testid="site-nav-productos">
              {lang === "es" ? "Productos" : "Products"}
            </Link>
            <Link to="/observatorio" className={linkCls("/observatorio")} data-testid="site-nav-observatorio">
              {lang === "es" ? "Observatorio · Investigaciones" : "Observatory · Research"}
            </Link>
            <Link to="/maestras-sombra" className={linkCls("/maestras-sombra")} data-testid="site-nav-maestras">
              {lang === "es" ? "Maestras Sombra Certificadas" : "Certified Shadow Teachers"}
            </Link>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={toggle}
              className="btn-ghost btn text-[12px] px-2.5 py-1.5"
              title="Idioma"
              data-testid="site-nav-lang-toggle"
            >
              <IconLanguage size={14} />
              <span className="font-mono uppercase tracking-widest text-[11px]">{lang === "es" ? "ES / EN" : "EN / ES"}</span>
            </button>
            {user && user.id ? (
              <button className="btn text-[12px] px-3 py-1.5" onClick={() => nav("/app")} data-testid="site-nav-app">
                {t("nav.enter")}
              </button>
            ) : (
              <>
                <button
                  className="btn-ghost btn text-[12px] px-3 py-1.5"
                  onClick={() => nav("/login")}
                  data-testid="site-nav-login"
                >
                  <IconLogin2 size={14} /> {t("nav.login")}
                </button>
                <button
                  className="btn text-[12px] px-3 py-1.5"
                  onClick={() => nav("/register")}
                  data-testid="site-nav-register"
                >
                  <IconUserPlus size={14} /> {t("nav.register")}
                </button>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 -mr-2 text-teal-deep"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            aria-expanded={open}
            data-testid="site-nav-mobile-toggle"
          >
            {open ? <IconX size={22} /> : <IconMenu2 size={22} />}
          </button>
        </div>
      </header>

      {/* Mobile menu panel */}
      {open && (
        <>
          <div
            className="md:hidden fixed inset-0 top-14 bg-black/30 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div
            className="md:hidden fixed top-14 left-0 right-0 z-40 bg-paper border-b border-line shadow-lg animate-[fadeIn_120ms_ease-out]"
            data-testid="site-nav-mobile-panel"
          >
            <Link to="/" className={mobileLinkCls("/")} data-testid="site-nav-home-mobile">{t("nav.home")}</Link>
            <Link to="/nosotros" className={mobileLinkCls("/nosotros")} data-testid="site-nav-about-mobile">{t("nav.about")}</Link>
            <Link to="/psicogadgets" className={mobileLinkCls("/psicogadgets")} data-testid="site-nav-gadgets-mobile">{t("nav.gadgets")}</Link>
            <Link to="/productos" className={mobileLinkCls("/productos")} data-testid="site-nav-productos-mobile">{lang === "es" ? "Productos" : "Products"}</Link>
            <Link to="/observatorio" className={mobileLinkCls("/observatorio")} data-testid="site-nav-observatorio-mobile">{lang === "es" ? "Observatorio · Investigaciones" : "Observatory · Research"}</Link>
            <Link to="/maestras-sombra" className={mobileLinkCls("/maestras-sombra")} data-testid="site-nav-maestras-mobile">{lang === "es" ? "Maestras Sombra Certificadas" : "Certified Shadow Teachers"}</Link>

            <div className="px-5 py-4 flex items-center gap-2 flex-wrap">
              <button
                onClick={toggle}
                className="btn-ghost btn text-[13px] px-3 py-2"
                data-testid="site-nav-lang-toggle-mobile"
              >
                <IconLanguage size={15} />
                <span className="font-mono uppercase tracking-widest text-[11px]">{lang === "es" ? "ES / EN" : "EN / ES"}</span>
              </button>
              {user && user.id ? (
                <button className="btn text-[13px] px-4 py-2 flex-1 min-w-[140px]" onClick={() => nav("/app")} data-testid="site-nav-app-mobile">
                  {t("nav.enter")}
                </button>
              ) : (
                <>
                  <button
                    className="btn-ghost btn text-[13px] px-3 py-2 flex-1 min-w-[130px]"
                    onClick={() => nav("/login")}
                    data-testid="site-nav-login-mobile"
                  >
                    <IconLogin2 size={15} /> {t("nav.login")}
                  </button>
                  <button
                    className="btn text-[13px] px-3 py-2 flex-1 min-w-[130px]"
                    onClick={() => nav("/register")}
                    data-testid="site-nav-register-mobile"
                  >
                    <IconUserPlus size={15} /> {t("nav.register")}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
}

export function BrandLogo({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      <circle cx="50" cy="50" r="36" fill="none" stroke="#0A3F33" strokeWidth="2.6" />
      <line x1="50" y1="50" x2="50" y2="14" stroke="#0A3F33" strokeWidth="1.8" />
      <line x1="50" y1="50" x2="81.2" y2="68" stroke="#0A3F33" strokeWidth="1.8" />
      <line x1="50" y1="50" x2="18.8" y2="68" stroke="#0A3F33" strokeWidth="1.8" />
      <circle cx="50" cy="14" r="5" fill="#0A3F33" />
      <circle cx="81.2" cy="68" r="5" fill="#0A3F33" />
      <circle cx="18.8" cy="68" r="5" fill="#0A3F33" />
      <circle cx="50" cy="50" r="10" fill="#C2872C" />
    </svg>
  );
}
